import React from "react";

const CELL = {
  saved: { cls: "ok", label: "Saved" },
  empty: { cls: "none", label: "0 rows" },
  no_payload: { cls: "red", label: "No data" },
  failed: { cls: "red", label: "Failed" },
  stuck: { cls: "red", label: "Stuck" },
  missing: { cls: "red", label: "Missing" },
  waiting: { cls: "amber", label: "Waiting" },
  not_yet: { cls: "none", label: "Not yet" },
};

const utcTime = (iso) =>
  iso ? new Date(iso).toLocaleTimeString("en-GB", { timeZone: "UTC", hour: "2-digit", minute: "2-digit" }) : "";

/** One cell = the day's status of one report for one store. Click opens its attempts. */
function Cell({ cell, reportName, onOpen, retrying }) {
  if (retrying) {
    return (
      <td className="rh-cell">
        <button type="button" className="rh-pill rh-pill--requested" onClick={onOpen} title={`${reportName}: being retried`}>
          <span className="ams-spin rh-spin" aria-hidden="true" />Retrying
        </button>
      </td>
    );
  }
  if (cell.state === "none") return <td className="rh-cell"><span className="rh-blank" aria-label="not used by this store">–</span></td>;
  const c = CELL[cell.state];
  const time = cell.state === "saved" ? utcTime(cell.savedAt) : "";
  const tip = `${reportName}: ${cell.text}${cell.attempts ? ` (${cell.attempts} log rows)` : ""}`;
  return (
    <td className="rh-cell">
      <button type="button" className={`rh-pill rh-pill--${c.cls}`} title={tip} onClick={onOpen} aria-label={tip}>
        {cell.state === "saved" ? <>✓ {time || "Saved"}</> : c.label}
        {cell.state === "saved" && cell.attempts > 2 && <span className="rh-retry" aria-hidden="true">↻</span>}
      </button>
    </td>
  );
}

export default function ReportMatrix({ columns, stores, onOpen, retrying, onRetryStore }) {
  // group header: consecutive columns of the same ad product
  const groups = [];
  columns.forEach((c) => {
    const last = groups[groups.length - 1];
    if (last && last.product === c.product) last.span += 1;
    else groups.push({ product: c.product, span: 1 });
  });

  return (
    <div className="ams-tablewrap rh-wrap">
      <table className="ams-table rh-table">
        <thead>
          <tr>
            <th rowSpan={2} className="rh-store-h">Store</th>
            <th rowSpan={2} className="ams-num">Saved</th>
            {groups.map((g, i) => (
              <th key={i} colSpan={g.span} className={`rh-group rh-group--${(g.product || "other").toLowerCase()}`}>
                {g.product === "SP" ? "Sponsored Products" : g.product === "SB" ? "Sponsored Brands"
                  : g.product === "SD" ? "Sponsored Display" : g.product === "STV" ? "Sponsored TV" : "Other"}
              </th>
            ))}
          </tr>
          <tr>
            {columns.map((c) => <th key={c.name} className="rh-col" title={c.name}>{c.short}</th>)}
          </tr>
        </thead>
        <tbody>
          {stores.length ? stores.map((s) => {
            const lvl = s.severity === 2 ? "red" : s.severity === 1 ? "amber" : "ok";
            return (
              <tr key={s.profileId} className={`ams-edge--${lvl}`}>
                <td className="ams-name rh-store">
                  {s.storeName || <span className="ams-dash">Unknown store</span>}
                  <dl className="ams-idlist">
                    {s.market && <><dt>Market</dt><dd>{s.market}</dd></>}
                    <dt>Profile</dt><dd>{s.profileId}</dd>
                  </dl>
                  {s.issue && <div className="rh-store-issue">{s.issue}</div>}
                  {onRetryStore && s.retryable > 0 && (
                    <button type="button" className="ams-link rh-store-retry" onClick={() => onRetryStore(s)}>
                      Retry {s.retryable} failed
                    </button>
                  )}
                </td>
                <td className={`ams-num rh-saved${s.issue ? " ams-bad" : s.counts.saved < s.expected ? " ams-warn" : ""}`}>
                  {s.counts.saved}<span className="ams-dash"> / {s.expected}</span>
                </td>
                {columns.map((c) => (
                  <Cell key={c.name} cell={s.cells[c.name]} reportName={c.name} onOpen={() => onOpen(s, c)}
                        retrying={retrying.has(`${s.profileId}|${c.name}`)} />
                ))}
              </tr>
            );
          }) : (
            <tr><td colSpan={columns.length + 2} className="ams-empty">No stores match this filter.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
